import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import {
  File as FileIcon,
  Link as LinkIcon,
  Loader2,
  ImageIcon,
  Download,
  ExternalLink,
  Play,
} from "lucide-react";

interface MediaGalleryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chatId: string;
}

interface MessageRow {
  id: string;
  attachment_url: string | null;
  original_text: string | null;
  created_at: string | null;
  sender_id: string;
}

type FileKind = "image" | "video" | "audio" | "document";

const getFileKind = (url: string): FileKind => {
  const cleanUrl = url.replace("attachment:", "");
  const extension = cleanUrl.split(".").pop()?.toLowerCase()?.split("?")[0];
  if (["jpg", "jpeg", "png", "gif", "webp", "heic", "heif"].includes(extension || "")) return "image";
  if (["mp4", "mov"].includes(extension || "")) return "video";
  if (["webm", "mp3", "ogg", "m4a", "wav", "aac", "3gp", "amr"].includes(extension || "")) return "audio";
  return "document";
};

const URL_REGEX = /(https?:\/\/[^\s<>"']+)/gi;

const extractUrls = (text: string | null | undefined): string[] => {
  if (!text) return [];
  const matches = text.match(URL_REGEX);
  return matches ? Array.from(new Set(matches)) : [];
};

const getFileNameFromPath = (url: string): string => {
  const cleanUrl = url.replace("attachment:", "").split("?")[0];
  const parts = cleanUrl.split("/");
  return decodeURIComponent(parts[parts.length - 1] || "file");
};

const formatDate = (iso: string | null) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return "";
  }
};

// Resolve a chat-attachments path or full URL into a usable URL
const resolveSignedUrl = async (attachmentUrl: string): Promise<string | null> => {
  if (attachmentUrl.startsWith("http")) return attachmentUrl;
  const path = attachmentUrl.replace("attachment:", "");
  const { data, error } = await supabase.storage
    .from("chat-attachments")
    .createSignedUrl(path, 3600);
  if (error) {
    console.error("[MediaGallery] signed url error:", error);
    return null;
  }
  return data.signedUrl;
};

const ThumbImage = ({ attachmentUrl }: { attachmentUrl: string }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let active = true;
    resolveSignedUrl(attachmentUrl).then((u) => {
      if (active) setUrl(u);
    });
    return () => {
      active = false;
    };
  }, [attachmentUrl]);

  if (errored || (!url && url !== null)) {
    return (
      <div className="aspect-square w-full bg-muted flex items-center justify-center rounded-md">
        <ImageIcon className="h-6 w-6 text-muted-foreground" />
      </div>
    );
  }

  if (!url) {
    return (
      <div className="aspect-square w-full bg-muted flex items-center justify-center rounded-md">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => window.open(url, "_blank")}
      className="block aspect-square w-full overflow-hidden rounded-md bg-muted group relative"
    >
      <img
        src={url}
        alt="Shared media"
        loading="lazy"
        onError={() => setErrored(true)}
        className="h-full w-full object-cover transition-transform group-hover:scale-105"
      />
    </button>
  );
};

const ThumbVideo = ({ attachmentUrl }: { attachmentUrl: string }) => {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    resolveSignedUrl(attachmentUrl).then((u) => {
      if (active) setUrl(u);
    });
    return () => {
      active = false;
    };
  }, [attachmentUrl]);

  return (
    <button
      type="button"
      onClick={() => url && window.open(url, "_blank")}
      className="aspect-square w-full overflow-hidden rounded-md bg-muted relative flex items-center justify-center group"
    >
      {url ? (
        <video
          src={url}
          className="h-full w-full object-cover"
          preload="metadata"
          muted
        />
      ) : (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      )}
      <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
        <Play className="h-6 w-6 text-white" fill="currentColor" />
      </div>
    </button>
  );
};

const DocRow = ({ attachmentUrl, createdAt }: { attachmentUrl: string; createdAt: string | null }) => {
  const [url, setUrl] = useState<string | null>(null);
  const fileName = getFileNameFromPath(attachmentUrl);

  useEffect(() => {
    let active = true;
    resolveSignedUrl(attachmentUrl).then((u) => {
      if (active) setUrl(u);
    });
    return () => {
      active = false;
    };
  }, [attachmentUrl]);

  return (
    <a
      href={url ?? "#"}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => {
        if (!url) e.preventDefault();
      }}
      className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-accent transition-colors"
    >
      <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
        <FileIcon className="h-5 w-5 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{fileName}</p>
        <p className="text-xs text-muted-foreground">{formatDate(createdAt)}</p>
      </div>
      {url ? (
        <Download className="h-4 w-4 text-muted-foreground" />
      ) : (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      )}
    </a>
  );
};

const LinkRow = ({ url, createdAt }: { url: string; createdAt: string | null }) => {
  let host = url;
  try {
    host = new URL(url).hostname.replace(/^www\./, "");
  } catch {
    // ignore
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-accent transition-colors"
    >
      <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
        <LinkIcon className="h-5 w-5 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{host}</p>
        <p className="text-xs text-muted-foreground truncate">{url}</p>
        <p className="text-[10px] text-muted-foreground mt-0.5">{formatDate(createdAt)}</p>
      </div>
      <ExternalLink className="h-4 w-4 text-muted-foreground" />
    </a>
  );
};

export const MediaGalleryDialog = ({ open, onOpenChange, chatId }: MediaGalleryDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<MessageRow[]>([]);

  useEffect(() => {
    if (!open || !chatId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("messages")
        .select("id, attachment_url, original_text, created_at, sender_id")
        .eq("chat_id", chatId)
        .order("created_at", { ascending: false })
        .limit(500);
      if (cancelled) return;
      if (error) {
        console.error("[MediaGallery] fetch error:", error);
        setMessages([]);
      } else {
        setMessages((data as MessageRow[]) || []);
      }
      setLoading(false);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [open, chatId]);

  const { mediaItems, docItems, linkItems } = useMemo(() => {
    const media: { url: string; kind: FileKind; createdAt: string | null; id: string }[] = [];
    const docs: { url: string; createdAt: string | null; id: string }[] = [];
    const links: { url: string; createdAt: string | null; id: string }[] = [];

    for (const m of messages) {
      if (m.attachment_url) {
        const kind = getFileKind(m.attachment_url);
        if (kind === "image" || kind === "video") {
          media.push({ url: m.attachment_url, kind, createdAt: m.created_at, id: m.id });
        } else if (kind === "document") {
          docs.push({ url: m.attachment_url, createdAt: m.created_at, id: m.id });
        }
        // audio (voice messages) intentionally excluded from gallery
      }
      const urls = extractUrls(m.original_text);
      for (const u of urls) {
        links.push({ url: u, createdAt: m.created_at, id: `${m.id}-${u}` });
      }
    }

    return { mediaItems: media, docItems: docs, linkItems: links };
  }, [messages]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] sm:max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle>Shared in this chat</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="media" className="flex-1 flex flex-col overflow-hidden">
          <div className="px-6">
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="media">
                Media{mediaItems.length > 0 ? ` (${mediaItems.length})` : ""}
              </TabsTrigger>
              <TabsTrigger value="links">
                Links{linkItems.length > 0 ? ` (${linkItems.length})` : ""}
              </TabsTrigger>
              <TabsTrigger value="docs">
                Docs{docItems.length > 0 ? ` (${docItems.length})` : ""}
              </TabsTrigger>
            </TabsList>
          </div>

          <ScrollArea className="flex-1">
            <div className="px-6 py-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  <TabsContent value="media" className="mt-0">
                    {mediaItems.length === 0 ? (
                      <EmptyState icon={<ImageIcon className="h-8 w-8" />} text="No images or videos shared yet." />
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {mediaItems.map((item) =>
                          item.kind === "image" ? (
                            <ThumbImage key={item.id} attachmentUrl={item.url} />
                          ) : (
                            <ThumbVideo key={item.id} attachmentUrl={item.url} />
                          )
                        )}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="links" className="mt-0">
                    {linkItems.length === 0 ? (
                      <EmptyState icon={<LinkIcon className="h-8 w-8" />} text="No links shared yet." />
                    ) : (
                      <div className="space-y-2">
                        {linkItems.map((item) => (
                          <LinkRow key={item.id} url={item.url} createdAt={item.createdAt} />
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="docs" className="mt-0">
                    {docItems.length === 0 ? (
                      <EmptyState icon={<FileIcon className="h-8 w-8" />} text="No documents shared yet." />
                    ) : (
                      <div className="space-y-2">
                        {docItems.map((item) => (
                          <DocRow key={item.id} attachmentUrl={item.url} createdAt={item.createdAt} />
                        ))}
                      </div>
                    )}
                  </TabsContent>
                </>
              )}
            </div>
          </ScrollArea>
        </Tabs>

        <div className="px-6 py-3 border-t flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const EmptyState = ({ icon, text }: { icon: React.ReactNode; text: string }) => (
  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
    <div className="mb-2 opacity-60">{icon}</div>
    <p className="text-sm">{text}</p>
  </div>
);
